<template>
  <div class="home-control" v-if="isLoaded">
    <h1>🧙🏼‍♂️ Home Control</h1>

    <!-- Contenedor de categorías draggable -->
    <div v-if="groupedDevices.length > 0">
      <div class="categories-container">
        <draggable
          v-model="groupedDevices"
          item-key="category"
          group="categories"
          @end="saveOrder"
          handle=".drag-handle"
          class="category-grid"
        >
          <template #item="{ element: category }">
            <div class="device-category-container">
              <h2>
                <span class="drag-handle"><i class="pi pi-ellipsis-v"></i></span>
                {{ category.category }}
              </h2>

              <!-- Contenedor de dispositivos draggable -->
              <draggable
                v-model="category.devices"
                item-key="id"
                group="devices"
                @end="saveOrder"
                handle=".drag-handle"
                class="device-grid"
                :forceFallback="true"
                :fallbackOnBody="true"
                :move="checkMove"
              >
                <template #item="{ element: device }">
                  <div class="device-container">
                    <div class="device-type-icon">
                      <i :class="getIcon(device)"></i>
                    </div>

                    <span class="drag-handle"><i class="pi pi-ellipsis-v"></i></span>

                    <NotificationBell
                      v-if="hasNotificationCapabilities(device)"
                      :device-id="device.deviceId"
                      :device-name="device.deviceName"
                      :capabilities="device.notificationCapabilities"
                    />

                    <div class="device-content">
                      <component
                        v-if="getComponent(device.type)"
                        :is="getComponent(device.type)"
                        :key="device.id"
                        v-bind="getComponentProps(device)"
                      />
                    </div>
                  </div>
                </template>
              </draggable>
            </div>
          </template>
        </draggable>
      </div>
    </div>

    <div v-else class="no-data-overlay">
      <span class="info-icon">ℹ️</span>
      <p>No hay dispositivos disponibles para su usuario.</p>
    </div>
  </div>
</template>

<script>
import { getLayout, saveLayout } from '../api/auth';
import { getDevices } from '../api/devices';
import componentMap from '../components/devices/DeviceMapper';
import draggable from 'vuedraggable';
import { useSessionStore } from '../store/mainStore';
import NotificationBell from '@/components/notifications/NotificationBell.vue';

export default {
  components: { draggable, NotificationBell },

  data() {
    return {
      groupedDevices: [],
      isLoaded: false
    };
  },

  async mounted() {
    console.log("Componentes mapeados:", componentMap);
    await this.syncLayout();
  },

  methods: {
    toPersistableLayout(layout) {
      return layout.map(category => ({
        category: category.category,
        devices: category.devices.map(device => ({
          id: device.id,
          type: device.type,
          deviceId: device.deviceId,
          deviceName: device.deviceName,
          ...(Array.isArray(device.cameraIds) && device.cameraIds.length > 0
            ? { cameraIds: device.cameraIds }
            : {})
        }))
      }));
    },

    async syncLayout() {
      const sessionStore = useSessionStore();

      try {
        sessionStore.setLoading(true);

        console.log("🚀 Iniciando sincronización del layout...");

        // 1) Obtener layout guardado
        const layoutResponse = await getLayout();
        const savedLayout = layoutResponse.data.layout || [];
        console.log("🗄 Layout guardado en BBDD:", JSON.stringify(savedLayout, null, 2));

        // 2) Obtener dispositivos visibles actuales desde el sistema
        const devicesResponse = await getDevices();
        const systemDevices = devicesResponse.devices || [];
        console.log("💾 Dispositivos actuales en el sistema:", JSON.stringify(systemDevices, null, 2));

        // 3) Mapa por ID real del dispositivo visible
        const systemMap = Object.fromEntries(
          systemDevices.map(dev => [dev.id, dev])
        );

        const usedSystemIds = new Set();
        const layoutGrouped = [];

        // 4) Reconstruir layout guardado respetando orden y categorías
        for (const savedCategory of savedLayout) {
          const validDevices = [];

          for (const savedDevice of (savedCategory.devices || [])) {
            // Intento principal: emparejar por deviceId real
            let match = systemMap[savedDevice.deviceId];

            // Fallback defensivo para layouts antiguos
            if (!match) {
              match = systemDevices.find(dev =>
                dev.type === savedDevice.type &&
                dev.name === savedDevice.deviceName
              );
            }

            if (!match) continue;

            validDevices.push({
              id: savedDevice.id,       // ID interno del layout
              type: match.type,
              deviceId: match.id,       // ID real del dispositivo
              deviceName: match.name,
              notificationCapabilities: Array.isArray(match.notificationCapabilities)
                ? match.notificationCapabilities
                : [],
              ...(Array.isArray(match.cameraIds) && match.cameraIds.length > 0
                ? { cameraIds: match.cameraIds }
                : {})
            });

            usedSystemIds.add(match.id);
          }

          if (validDevices.length > 0) {
            layoutGrouped.push({
              category: savedCategory.category,
              devices: validDevices
            });
          }
        }

        // 5) Agregar dispositivos nuevos que no estaban en el layout guardado
        let tempIdCounter = 0;

        for (const dev of systemDevices) {
          if (usedSystemIds.has(dev.id)) continue;

          let categoryGroup = layoutGrouped.find(g => g.category === dev.category);
          if (!categoryGroup) {
            categoryGroup = {
              category: dev.category,
              devices: []
            };
            layoutGrouped.push(categoryGroup);
          }

          categoryGroup.devices.push({
            id: `temp-id-${tempIdCounter++}`,
            type: dev.type,
            deviceId: dev.id,
            deviceName: dev.name,
            notificationCapabilities: Array.isArray(dev.notificationCapabilities)
              ? dev.notificationCapabilities
              : [],
            ...(Array.isArray(dev.cameraIds) && dev.cameraIds.length > 0
              ? { cameraIds: dev.cameraIds }
              : {})
          });
        }

        console.log("📌 Layout ajustado final (orden y dispositivos):", JSON.stringify(layoutGrouped, null, 2));

        // 6) Guardar si hubo cambios
        const persistableLayout = this.toPersistableLayout(layoutGrouped);
        const layoutChanged = JSON.stringify(persistableLayout) !== JSON.stringify(savedLayout);

        if (layoutChanged) {
          console.log("⚡️ Layout cambiado, actualizando en base de datos...");
          await saveLayout(persistableLayout);
        } else {
          console.log("✅ Layout ya estaba sincronizado, sin cambios.");
        }

        this.groupedDevices = layoutGrouped;
      } catch (error) {
        console.error("❌ Error al sincronizar el layout:", error);
      } finally {
        this.isLoaded = true;
        sessionStore.setLoading(false);
        console.log("🎯 Sincronización terminada.");
      }
    },

    getComponent(tipo) {
      return componentMap[tipo] || null;
    },

    getComponentProps(device) {
      const baseProps = {
        deviceId: device.deviceId,
        deviceName: device.deviceName
      };

      if (device.type === 'IPCameraDevice' && Array.isArray(device.cameraIds) && device.cameraIds.length > 0) {
        return {
          ...baseProps,
          cameraIds: device.cameraIds
        };
      }

      return baseProps;
    },

    getIcon(device) {
      if (device.type === 'InfoDevice') {
        console.log('Icon heuristics →', {
          type: device.type,
          deviceId: device.deviceId,
          deviceName: device.deviceName
        });

        const text = (device.deviceId || device.deviceName || '').toLowerCase();

        if (
          text.includes('temp') ||
          text.includes('temperatura') ||
          text.includes('hum') ||
          text.includes('humedad') ||
          text.includes('aht10') ||
          text.includes('dht22')
        ) {
          return 'pi pi-gauge';
        }

        return 'pi pi-shield';
      }

      switch (device.type) {
        case 'BulbRGBDevice': return 'pi pi-lightbulb';
        case 'SwitchDevice': return 'pi pi-power-off';
        case 'IrrigationDevice': return 'pi pi-sliders-h';
        case 'AirConditioningDevice': return 'pi pi-sun';
        case 'AlarmDevice': return 'pi pi-bell';
        case 'CameraDevice': return 'pi pi-camera';
        case 'IPCameraDevice': return 'pi pi-camera';
        default: return '';
      }
    },

    checkMove(event) {
      return event.to === event.from;
    },

    async saveOrder() {
      try {
        await saveLayout(this.toPersistableLayout(this.groupedDevices));
        console.log("Esquema de usuario guardado correctamente");
      } catch (error) {
        console.error("Error al guardar el esquema:", error);
      }
    },

    hasNotificationCapabilities(device) {
      return Array.isArray(device.notificationCapabilities)
        && device.notificationCapabilities.length > 0;
    }
  }
};
</script>

<!-- Importa los estilos de HomeControlView -->
<style src="@/assets/css/HomeControlView.css" scoped></style>